import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NewDealCard from '../components/deals/NewDealCard';
import { DealProps } from '../types/deal';
import AdPlaceholder from '../components/ads/AdPlaceholder';
import TrendingDeals from '../components/deals/TrendingDeals';
import { getDeals } from '../services/dealService';
import { useBulkDealLoader } from '../hooks/useBulkDealLoader';
import DealCardSkeleton from '../components/deals/DealCardSkeleton';
import { lcpPreloader } from '../utils/lcpPreloader';
import './Home.css';

// Responsive deals per page - more on mobile, 4 rows on desktop
const getDealsPerPage = () => {
  if (typeof window !== 'undefined') {
    const width = window.innerWidth;
    if (width < 480) {
      return 33; // Very small mobile: 33 cards (11 rows x 3 columns)
    } else if (width < 768) {
      return 32; // Mobile: 32 cards (8 rows x 4 columns)
    } else if (width < 1024) {
      return 24; // Tablet: 24 cards (6 rows x 4 columns)
    } else if (width < 1400) {
      return 16; // Desktop: 16 cards (4 rows x 4 columns)
    } else if (width < 1600) {
      return 20; // Large desktop: 20 cards (4 rows x 5 columns)
    } else {
      return 24; // Extra large desktop: 24 cards (4 rows x 6 columns)
    }
  }
  return 32; // Default to mobile count
};

// Debounce utility for resize events
const useDebounce = (callback: () => void, delay: number) => {
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  const debouncedCallback = useCallback(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const newTimer = setTimeout(() => {
      callback();
      setDebounceTimer(null);
    }, delay);

    setDebounceTimer(newTimer);
  }, [callback, delay, debounceTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  return debouncedCallback;
};

const Home: React.FC = () => {
  const [deals, setDeals] = useState<DealProps[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [showRightSidebar, setShowRightSidebar] = useState(false);
  const [dealsPerPage, setDealsPerPage] = useState(getDealsPerPage());

  // Trigger bulk loading of deal data when deals are loaded
  useBulkDealLoader(deals);

  // Memoize screen size calculations to prevent unnecessary re-renders
  const screenSizeConfig = useMemo(() => {
    if (typeof window === 'undefined')
      return { isMobile: false, showRightSidebar: false, dealsPerPage: 32 };

    const width = window.innerWidth;
    return {
      isMobile: width <= 899,
      showRightSidebar: width >= 1200,
      dealsPerPage: getDealsPerPage(),
    };
  }, []);

  // Debounced resize handler to prevent excessive API calls
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    const newIsMobile = width <= 899;
    const newShowRightSidebar = width >= 1200;
    const newDealsPerPage = getDealsPerPage();

    setIsMobile(newIsMobile);
    setShowRightSidebar(newShowRightSidebar);

    // Only update deals per page if it actually changed
    if (newDealsPerPage !== dealsPerPage) {
      setDealsPerPage(newDealsPerPage);
      setCurrentPage(1); // Reset to first page when changing layout
    }
  }, [dealsPerPage]);

  // Debounced resize handler with 250ms delay
  const debouncedResize = useDebounce(handleResize, 250);

  // Consolidated useEffect for initialization and resize handling
  useEffect(() => {
    // Initialize screen size on mount
    if (typeof window !== 'undefined') {
      const initialConfig = screenSizeConfig;
      setIsMobile(initialConfig.isMobile);
      setShowRightSidebar(initialConfig.showRightSidebar);
      setDealsPerPage(initialConfig.dealsPerPage);
    }

    // Add resize listener with debouncing
    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, [debouncedResize, screenSizeConfig]);

  // Optimized deals fetching with proper dependency management
  useEffect(() => {
    const fetchDeals = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await getDeals(currentPage, dealsPerPage);

        setDeals(result.deals || []);

        // Immediate LCP preloading for first deal with optimized URLs
        if (result.deals && result.deals[0]) {
          const firstDeal = result.deals[0];
          let imageUrl = null;

          if (firstDeal.optimizedImages?.[0]?.thumbnails?.medium?.jpeg) {
            imageUrl = firstDeal.optimizedImages[0].thumbnails.medium.jpeg;
          } else if (firstDeal.optimizedImages?.[0]?.jpeg) {
            imageUrl = firstDeal.optimizedImages[0].jpeg;
          } else if (firstDeal.imageUrl) {
            imageUrl = firstDeal.imageUrl;
          }

          // Use Firebase Storage URLs directly (no more URL construction issues)
          if (imageUrl) {
            lcpPreloader.preloadImage(imageUrl, { priority: 'high', crossorigin: 'anonymous' });
          }
        }

        // Calculate total pages
        if (result.pagination) {
          setTotalPages(Math.ceil(result.total / dealsPerPage));
        }
      } catch (err: any) {
        if (process.env.NODE_ENV === 'development') {
          console.error('🏠 Home: Error fetching deals:', err);
        }
        setError(err.message || 'Failed to fetch deals');
      } finally {
        setLoading(false);
      }
    };

    fetchDeals();
  }, [currentPage, dealsPerPage]); // Only depend on the actual values that should trigger refetch

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  // Organization and SearchAction schemas for homepage
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Deals n Steals',
    url: 'https://dealsnsteals.net',
    logo: 'https://dealsnsteals.net/logo.png',
    description:
      'Community-driven platform for finding the best deals and discounts online. Save on electronics, fashion, home goods, and more.',
    sameAs: [
      'https://twitter.com/dealsnsteals',
      'https://facebook.com/dealsnsteals',
      'https://instagram.com/dealsnsteals',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@dealsnsteals.net',
    },
  };

  const searchActionSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: 'https://dealsnsteals.net',
    name: 'Deals n Steals',
    description: 'Find the best deals and discounts online',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://dealsnsteals.net/search?q={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <div className="home-page">
      <Helmet>
        <title>Deals n Steals - Best Daily Deals & Discounts Online</title>
        <meta
          name="description"
          content="Discover amazing deals and save up to 80% on electronics, fashion, home goods, toys, and more. Community-voted best deals updated daily. Free shipping on many items."
        />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Deals n Steals - Best Daily Deals & Discounts" />
        <meta
          property="og:description"
          content="Save up to 80% on top brands. Community-voted deals on electronics, fashion, home goods, and more. Updated daily."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dealsnsteals.net" />
        <meta property="og:image" content="https://dealsnsteals.net/og-home-image.jpg" />
        <meta property="og:site_name" content="Deals n Steals" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@dealsnsteals" />
        <meta name="twitter:title" content="Deals n Steals - Best Daily Deals" />
        <meta
          name="twitter:description"
          content="Save up to 80% on electronics, fashion, home goods & more. Updated daily."
        />
        <meta name="twitter:image" content="https://dealsnsteals.net/twitter-home-image.jpg" />

        {/* Additional SEO */}
        <link rel="canonical" href="https://dealsnsteals.net" />
        <meta name="robots" content="index, follow" />

        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(searchActionSchema)}</script>
      </Helmet>

      <div className="compact-header">
        <h1>Today's Best Deals</h1>
      </div>

      <div className="home-content container">
        <section className="deals-section">
          {loading ? (
            <div className="loading-container">
              <div className="deals-grid responsive-grid">
                {Array.from({ length: dealsPerPage }).map((_, index) => (
                  <DealCardSkeleton key={index} />
                ))}
              </div>
            </div>
          ) : error ? (
            <div className="error-container">
              <p>Error: {error}</p>
              <button
                onClick={() => {
                  setError(null);
                  setCurrentPage(1);
                }}
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              {deals.length === 0 ? (
                <div className="no-deals-container">
                  <h3>No deals available</h3>
                  <p>Check back later for new deals!</p>
                </div>
              ) : (
                <>
                  <div className="deals-grid responsive-grid">
                    {/* Only show real deals from API */}
                    {deals.map((deal, index) => (
                      <NewDealCard
                        key={deal._id}
                        deal={deal}
                        priority={index < 4}
                        isLCP={index === 0} // Mark first deal as LCP candidate
                      />
                    ))}
                  </div>

                  {/* View All Deals Button */}
                  <div className="view-all-container">
                    <Link to="/deals" className="view-all-btn">
                      View All Deals
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 12H19M19 12L12 5M19 12L12 19"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Link>
                  </div>

                  {/* Pagination for home page (if needed) */}
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="pagination-btn"
                      >
                        Previous
                      </button>

                      <span className="page-info">
                        Page {currentPage} of {totalPages}
                      </span>

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="pagination-btn"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </section>

        {showRightSidebar && (
          <aside className="home-right-ads">
            <TrendingDeals deals={deals} loading={loading} error={error} />
            <AdPlaceholder type="tips" position="right" />
          </aside>
        )}
      </div>
    </div>
  );
};

export default Home;
